import React, { useState, useEffect } from 'react';

const VERIFICATION_STEPS = [
  { id: 1, label: 'Document Scan', desc: 'Scanning uploaded ID documents', duration: 1500 },
  { id: 2, label: 'Data Extraction', desc: 'Extracting identity information', duration: 2000 },
  { id: 3, label: 'Database Check', desc: 'Cross-referencing national registry', duration: 2500 },
  { id: 4, label: 'Fraud Detection', desc: 'Running security checks', duration: 1800 },
];

const IDVerificationStep = ({ onNext, onBack }) => {
  const [verificationState, setVerificationState] = useState('idle'); // idle | running | complete | failed
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);

  const runVerification = async () => {
    setVerificationState('running');
    setCompletedSteps([]);
    setCurrentStep(0);

    for (let i = 0; i < VERIFICATION_STEPS?.length; i++) {
      setCurrentStep(i);
      await new Promise((resolve) => setTimeout(resolve, VERIFICATION_STEPS[i].duration));
      setCompletedSteps((prev) => [...prev, VERIFICATION_STEPS?.[i]?.id]);
    }

    setVerificationState('complete');
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#0a1628' }}>
          ID Verification
        </h2>
        <p className="text-gray-500 text-sm">
          We'll verify your identity using the documents you uploaded. This takes about 10 seconds.
        </p>
      </div>
      {/* Verification Card */}
      <div
        className="rounded-2xl p-6 mb-6"
        style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%)' }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
            style={{ background: 'rgba(255,215,0,0.15)' }}
          >
            🔍
          </div>
          <div>
            <h3 className="font-bold text-white">Identity Verification</h3>
            <p className="text-xs" style={{ color: 'rgba(255,215,0,0.7)' }}>
              Powered by OnTheGo Verify
            </p>
          </div>
          {verificationState === 'complete' && (
            <div
              className="ml-auto px-3 py-1 rounded-full text-xs font-bold"
              style={{ background: 'rgba(255,215,0,0.2)', color: '#ffd700' }}
            >
              ✓ Verified
            </div>
          )}
        </div>

        <div className="space-y-3">
          {VERIFICATION_STEPS?.map((step, idx) => {
            const isCompleted = completedSteps?.includes(step?.id);
            const isActive = verificationState === 'running' && currentStep === idx;
            const isPending = verificationState === 'idle' || (!isCompleted && !isActive);

            return (
              <div key={step?.id} className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-all duration-300"
                  style={{
                    background: isCompleted
                      ? 'linear-gradient(135deg, #ffd700, #f59e0b)'
                      : isActive
                      ? 'rgba(255,215,0,0.3)'
                      : 'rgba(255,255,255,0.1)',
                    color: isCompleted ? '#0a1628' : '#ffd700',
                  }}
                >
                  {isCompleted ? '✓' : isActive ? (
                    <div className="w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    step?.id
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold"
                    style={{ color: isCompleted ? '#ffd700' : isActive ? 'white' : 'rgba(255,255,255,0.4)' }}
                  >
                    {step?.label}
                  </p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {step?.desc}
                  </p>
                </div>
                {isActive && (
                  <div className="w-16 h-1 rounded-full overflow-hidden bg-white/10">
                    <div
                      className="h-full rounded-full animate-pulse"
                      style={{ background: '#ffd700', width: '60%' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Status Message */}
      {verificationState === 'complete' && (
        <div
          className="rounded-xl p-4 mb-6 flex items-center gap-3"
          style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)' }}
        >
          <span className="text-2xl">✅</span>
          <div>
            <p className="font-bold text-sm" style={{ color: '#0a1628' }}>
              Identity Verified Successfully
            </p>
            <p className="text-xs text-gray-500">
              Your documents have been verified. You can proceed to the next step.
            </p>
          </div>
        </div>
      )}
      {verificationState === 'idle' && (
        <div
          className="rounded-xl p-4 mb-6 flex items-center gap-3"
          style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}
        >
          <span className="text-2xl">ℹ️</span>
          <p className="text-sm text-gray-500">
            Click "Start Verification" to begin the automated identity check process.
          </p>
        </div>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3.5 rounded-xl font-bold text-sm border-2 transition-all duration-200 hover:bg-gray-50"
          style={{ borderColor: '#0a1628', color: '#0a1628' }}
        >
          ← Back
        </button>
        {verificationState !== 'complete' ? (
          <button
            type="button"
            onClick={runVerification}
            disabled={verificationState === 'running'}
            className="flex-1 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-95 shadow-lg disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #ffd700, #f59e0b)', color: '#0a1628' }}
          >
            {verificationState === 'running' ? 'Verifying...' : 'Start Verification'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            className="flex-1 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-95 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #ffd700, #f59e0b)', color: '#0a1628' }}
          >
            Continue →
          </button>
        )}
      </div>
    </div>
  );
};

export default IDVerificationStep;
